import React, { useState, useCallback, useEffect, useRef } from 'react';
import { CloudArrowUpIcon, DocumentIcon, XMarkIcon, EyeIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useUploadDocumentMutation, useDeleteDocumentMutation, useLazyDownloadDocumentQuery } from '../services/documentApi';
import Modal from './common/Modal';
import { Button } from './common/Button';

const DOCUMENT_TYPES = [
  { value: 'resume', label: 'Resume/CV', required: true },
  { value: 'id_card', label: 'ID Card', required: false },
  { value: 'photo', label: 'Profile Photo', required: false },
  { value: 'aadhar', label: 'Aadhar Card', required: false },
  { value: 'pan', label: 'PAN Card', required: false },
  { value: 'passport', label: 'Passport', required: false },
  { value: 'visa', label: 'Visa Documents', required: false },
  { value: 'degree', label: 'Degree Certificates', required: false },
  { value: 'experience', label: 'Experience Letters', required: false },
  { value: 'bank_statement', label: 'Bank Statements', required: false },
  { value: 'salary_slip', label: 'Salary Slips', required: false },
  { value: 'offer_letter', label: 'Offer Letter', required: false },
  { value: 'other', label: 'Other Documents', required: false }
];

const DocumentUpload = ({ employeeId, documents = [], onDocumentsChange, required = false }) => {
  // Ensure documents is always an array
  const safeDocuments = Array.isArray(documents) ? documents : [];
  const [uploadDocument, { isLoading: isUploading }] = useUploadDocumentMutation();
  const [deleteDocument, { isLoading: isDeleting }] = useDeleteDocumentMutation();
  const [dragActive, setDragActive] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const downloadUrlRef = useRef(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  // Lazy query to fetch protected download blobs with auth headers
  const [triggerDownload, { data: downloadBlob, isFetching: isDownloading }] = useLazyDownloadDocumentQuery();

  const handleDirectDownload = async (document) => {
    if (!document || !document._id) return;

    try {
      // Store document info for download filename (not preview)
      downloadUrlRef.current = { document, isPreview: false };
      // Trigger the download
      await triggerDownload(document._id);
    } catch (error) {
      console.error('Download failed:', error);
      alert(`Failed to download ${document.originalName}: ${error.data?.message || 'Unknown error'}`);
    }
  };

  const handlePreviewOrDownload = async (document) => {
    if (!document || !document._id) return;

    try {
      // Store document info for preview (will open in new tab)
      downloadUrlRef.current = { document, isPreview: true };
      // Trigger the download to get the blob for preview
      await triggerDownload(document._id);
    } catch (error) {
      console.error('Preview/Download failed:', error);
      alert(`Failed to open ${document.originalName}: ${error.data?.message || 'Unknown error'}`);
    }
  };

  const handleFiles = useCallback(async (files) => {
    for (const file of files) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        alert(`File ${file.name} is too large. Maximum size is 2MB.`);
        continue;
      }

      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];

      if (!allowedTypes.includes(file.type)) {
        alert(`File ${file.name} has unsupported format. Please upload PDF, DOC, DOCX, or image files.`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('document', file);
        formData.append('documentType', selectedType);
        formData.append('description', `Employee ${selectedType} document`);

        const result = await uploadDocument({ formData, employeeId }).unwrap();

        // Add the new document to the list (with safety check)
        if (result?.document) {
          const newDocuments = [...safeDocuments, result.document];
          onDocumentsChange(newDocuments);
        }

        setSelectedType(''); // Reset type selection
      } catch (error) {
        console.error('Upload failed:', error);
        alert(`Failed to upload ${file.name}: ${error.data?.message || 'Unknown error'}`);
      }
    }
  }, [selectedType, safeDocuments, onDocumentsChange, uploadDocument, employeeId]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (!selectedType) {
      alert('Please select a document type first');
      return;
    }

    const files = [...e.dataTransfer.files];
    await handleFiles(files);
  }, [selectedType, handleFiles]);

  const handleFileInput = async (e) => {
    if (!selectedType) {
      alert('Please select a document type first');
      return;
    }

    const files = [...e.target.files];
    await handleFiles(files);
    e.target.value = ''; // Reset input
  };

  const handleDelete = async (documentId) => {
    const document = safeDocuments.find(doc => doc._id === documentId);
    if (!document) return;

    setDocumentToDelete(document);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!documentToDelete) return;

    try {
      await deleteDocument(documentToDelete._id).unwrap();
      const updatedDocuments = safeDocuments.filter(doc => doc._id !== documentToDelete._id);
      onDocumentsChange(updatedDocuments);
      setDeleteModalOpen(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error('Delete failed:', error);
      alert(`Failed to delete document: ${error.data?.message || 'Unknown error'}`);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setDocumentToDelete(null);
  };

  // Download handling: when download blob arrives, create a link and trigger download or open in new tab
  useEffect(() => {
    if (downloadBlob instanceof Blob && downloadBlob.size > 0) {
      const url = URL.createObjectURL(downloadBlob);
      const document = downloadUrlRef.current?.document;

      // For preview, try to open in new tab, fallback to download
      if (downloadUrlRef.current?.isPreview) {
        const newTab = window.open(url, '_blank');
        if (!newTab) {
          // If popup blocked, fallback to download
          const a = document.createElement('a');
          a.href = url;
          a.download = document?.originalName || 'download';
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
      } else {
        // Regular download
        const a = document.createElement('a');
        a.href = url;
        a.download = document?.originalName || 'download';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }

      // revoke after short timeout to ensure download/preview starts
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    }
  }, [downloadBlob]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Documents {required && <span className="text-red-500">*</span>}
        </label>
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select Type</option>
          {DOCUMENT_TYPES.map(type => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          onChange={handleFileInput}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={!selectedType || isUploading}
        />

        <div className="space-y-2">
          <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
          <div>
            <p className="text-sm text-gray-600">
              {selectedType
                ? `Drop ${DOCUMENT_TYPES.find(t => t.value === selectedType)?.label} files here, or click to select`
                : 'Select document type first, then drop files here or click to select'
              }
            </p>
            <p className="text-xs text-gray-500 mt-1">
              PDF, DOC, DOCX, JPG, PNG up to 2MB each
            </p>
          </div>
          {isUploading && (
            <div className="text-sm text-blue-600">Uploading...</div>
          )}
        </div>
      </div>

      {/* Document List */}
      {safeDocuments.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-800 flex items-center">
            <DocumentIcon className="h-4 w-4 mr-2 text-blue-600" />
            Uploaded Documents ({safeDocuments.length})
          </h4>
          <div className="grid gap-3">
            {safeDocuments.filter(doc => doc && doc._id).map((doc, index) => (
              <div
                key={doc._id}
                className="group relative bg-gradient-to-r from-white to-gray-50 border border-gray-200 rounded-xl p-4 hover:shadow-lg hover:border-blue-300 transition-all duration-300 transform hover:-translate-y-1 animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Document Type Badge */}
                <div className="absolute top-3 right-3">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {DOCUMENT_TYPES.find(t => t.value === doc.documentType)?.label || doc.documentType || 'Document'}
                  </span>
                </div>

                <div className="flex items-center space-x-4 pr-20">
                  {/* File Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow duration-300">
                      <DocumentIcon className="h-6 w-6 text-white" />
                    </div>
                  </div>

                  {/* Document Info */}
                  <div className="flex-1 min-w-0">
                    <h5 className="text-sm font-semibold text-gray-900 truncate group-hover:text-blue-700 transition-colors duration-200">
                      {doc.originalName || 'Unknown file'}
                    </h5>
                    <div className="flex items-center space-x-4 mt-1">
                      <span className="text-xs text-gray-500">
                        {doc.size ? formatFileSize(doc.size) : 'Unknown size'}
                      </span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500">
                        {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : 'Unknown date'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="absolute bottom-4 right-4 flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button
                    type="button"
                    onClick={() => handlePreviewOrDownload(doc)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 transform hover:scale-110"
                    title="Preview in New Tab"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDirectDownload(doc)}
                    disabled={isDownloading}
                    className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200 transform hover:scale-110 disabled:opacity-50"
                    title="Download"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(doc._id)}
                    disabled={isDeleting}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 transform hover:scale-110 disabled:opacity-50"
                    title="Delete"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>

                {/* Hover Effect Overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={cancelDelete}
        title="Delete Document"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <XMarkIcon className="h-6 w-6 text-red-500" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-gray-900">
                Delete Document
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete "{documentToDelete?.originalName}"? This action cannot be undone.
                </p>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={cancelDelete}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DocumentUpload;
